import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { useIntl } from "../../../contexts/IntlContext";
import { useFormHandler } from "../../../hooks/useFormHandler";
import { useCountry } from "../../../contexts/CountryContext";
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid2, MenuItem, Typography } from "@mui/material";
import IconButton from "../../../components/ui/IconButton";
import { Delete, Add, ExpandMore } from "@mui/icons-material";
import Input from "../../../components/ui/Input";
import FormContainer from "../../../components/ui/FormContainer";
import { PageHeader } from "../../../components/PageHeader";
import { Breadcrumbs } from "../../../components/Breadcrumbs";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { GLOBAL_ROLES } from "../../../constants/roles";
import { countries } from "../../../data/countries";
import { RoleGuard } from "../../../components/ui/RoleGuard";
import { ThumbnailLogoCard } from "./ThumbnailLogoCard";

interface Channel {
  id?: string;
  name: string;
}

interface EstablishmentFormData {
  id?: string;
  name: string;
  country: string;
  state: string;
  city: string;
  channels?: Channel[];
  toDelete?: {
    channels: Channel[];
  };
  timezone: string;
  thumbnailLogoUrl?: string | null;
}

export function EstablishmentForm() {
  const { country } = useCountry();
  const intl = useIntl();
  const navigate = useNavigate();
  // logo editor removido para não permitir posicionamento via estabelecimento

  const {
    formData,
    updateField,
    handleSubmit,
    loading,
    isEdit,
    unsavedChangesBlocked,
    confirmDiscardChanges,
    cancelDiscardChanges,
    fieldErrors,
  } =
    useFormHandler<EstablishmentFormData>({
      baseEndpoint: "/app/establishments",
      redirectPath: "/estabelecimentos",
      defaultData: {
        name: "",
        country: country || "",
        state: "",
        city: "",
        timezone: "",
        channels: [{ name: "" }],
        toDelete: { channels: [] },
      },
      successMessageId: "establishmentForm.success",
      errorMessageId: "establishmentForm.error",
      validateFields: (data): { valid: boolean } => {
        const channelsValid =
          data.channels?.every((channel) => !!channel.name) ?? true;
        return {
          valid:
            !!data.name &&
            !!data.country &&
            !!data.state &&
            !!data.city &&
            channelsValid,
        };
      },
      transformData: (data) => ({
        name: data.name,
        country: data.country,
        state: data.state,
        city: data.city,
        timezone: data.timezone,
        channels: data.channels || [{ name: "" }],
        ...(data.toDelete?.channels?.length && { toDelete: data.toDelete }),
      }),
      loadData: async (id: string) => {
        const response = await api.get(`/app/establishments/${id}`);
        const establishmentData = response.data;
        return {
          id: establishmentData.id,
          name: establishmentData.name,
          country: establishmentData.country,
          state: establishmentData.state,
          city: establishmentData.city,
          timezone: establishmentData.timezone,
          thumbnailLogoUrl: establishmentData.thumbnailLogoUrl ?? null,
          channels: establishmentData.channels || [],
          toDelete: { channels: [] },
        };
      },
    });

  useEffect(() => {
    if (country && !isEdit) {
      updateField("country", country);
    }
  }, [country, isEdit, updateField]);

  useEffect(() => {
    if (!isEdit) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) updateField("timezone", tz);
      } catch {
        // fallback: user selects manually
      }
    }
  }, [isEdit, updateField]);

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));
  const requiredFieldError = (field: string, value: unknown, label: string) => {
    if (fieldErrors[field]) return fieldErrors[field];
    return touched[field] && !value
      ? intl.formatMessage({ id: "validation.required" }, { field: label })
      : "";
  };

  const selectedCountry =
    countries.find((c) => c.value === formData.country) || countries[0];

  const handleCountryChange = (countryCode: string) => {
    updateField("country", countryCode);
    updateField("state", "");
    updateField("city", "");
    updateField("timezone", "");
  };

  const handleAddChannel = () => {
    const newChannel: Channel = {
      name: "",
    };
    const updatedChannels = [...(formData.channels || []), newChannel];
    updateField("channels", updatedChannels);
  };

  const handleUpdateChannelName = (index: number, newName: string) => {
    const updatedChannels = [...(formData.channels || [])];
    updatedChannels[index] = { ...updatedChannels[index], name: newName };
    updateField("channels", updatedChannels);
  };

  const [channelToDeleteIndex, setChannelToDeleteIndex] = useState<number | null>(null);
  const [channelsExpanded, setChannelsExpanded] = useState(false);

  const confirmDeleteChannel = (index: number) => {
    const updatedChannels = formData.channels?.filter((_, i) => i !== index);
    updateField("channels", updatedChannels);

    const channel = formData.channels?.[index];
    if (channel?.id) {
      const updatedToDelete = {
        channels: [...(formData.toDelete?.channels || []), channel],
      };
      updateField("toDelete", updatedToDelete);
    }
  };

  const handleDeleteChannel = (index: number) => {
    const channel = formData.channels?.[index];
    if (channel?.id) {
      setChannelToDeleteIndex(index);
      return;
    }
    confirmDeleteChannel(index);
  };

  const handleFormSubmit = async (event: any, continueEditing?: boolean) => {
    setTouched({
      name: true,
      country: true,
      state: true,
      city: true,
      timezone: true,
    });
    const hasEmptyChannel = formData.channels?.some((channel) => !channel.name);
    if (hasEmptyChannel) {
      setChannelsExpanded(true);
    }
    await handleSubmit(event, continueEditing);
  };

  return (
    <>
      <PageHeader
        title={intl.formatMessage({ id: "establishmentForm.title" })}
        breadcrumb={
          <Breadcrumbs
            items={[
              { label: intl.formatMessage({ id: "cameras.title" }), onClick: () => navigate("/cameras") },
              {
                label: isEdit
                  ? formData.name || intl.formatMessage({ id: "establishmentForm.title" })
                  : intl.formatMessage({ id: "establishmentForm.new" }),
              },
            ]}
            onBack={() => navigate("/cameras")}
          />
        }
      />
      <FormContainer
        onSubmit={handleFormSubmit}
        continueEditing={true}
        loading={loading}
        submitText={isEdit ? "common.update" : "establishmentForm.create"}
        redirectPath="/estabelecimentos"
        unsavedChangesBlocked={unsavedChangesBlocked}
        onConfirmDiscardChanges={confirmDiscardChanges}
        onCancelDiscardChanges={cancelDiscardChanges}
      >
      <Input
        label={intl.formatMessage({ id: "common.establishmentName" })}
        value={formData.name}
        onChange={(e) => updateField("name", e.target.value)}
        onBlur={() => markTouched("name")}
        error={!!requiredFieldError("name", formData.name, intl.formatMessage({ id: "common.establishmentName" }))}
        helperText={requiredFieldError("name", formData.name, intl.formatMessage({ id: "common.establishmentName" }))}
        required
      />

      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <Input
            label={intl.formatMessage({ id: "common.country" })}
            select
            value={formData.country}
            onChange={(e) => handleCountryChange(e.target.value)}
            onBlur={() => markTouched("country")}
            error={!!requiredFieldError("country", formData.country, intl.formatMessage({ id: "common.country" }))}
            helperText={requiredFieldError("country", formData.country, intl.formatMessage({ id: "common.country" }))}
            required
          >
            {countries.map((country) => (
              <MenuItem key={country.value} value={country.value}>
                {country.label}
              </MenuItem>
            ))}
          </Input>
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <Input
            label={intl.formatMessage({ id: "common.state" })}
            select
            value={formData.state || ""}
            onChange={(e) => updateField("state", e.target.value)}
            onBlur={() => markTouched("state")}
            error={!!requiredFieldError("state", formData.state, intl.formatMessage({ id: "common.state" }))}
            helperText={requiredFieldError("state", formData.state, intl.formatMessage({ id: "common.state" }))}
            required
          >
            {selectedCountry.states.map((state) => (
              <MenuItem key={state.value} value={state.value}>
                {state.label}
              </MenuItem>
            ))}
          </Input>
        </Grid2>
      </Grid2>

      <Input
        label={intl.formatMessage({ id: "common.city" })}
        value={formData.city}
        onChange={(e) => updateField("city", e.target.value)}
        onBlur={() => markTouched("city")}
        error={!!requiredFieldError("city", formData.city, intl.formatMessage({ id: "common.city" }))}
        helperText={requiredFieldError("city", formData.city, intl.formatMessage({ id: "common.city" }))}
        required
      />

      {/* Gestão de visitantes por estabelecimento removida */}
      <Input
        label={intl.formatMessage({ id: "establishmentForm.timezone" })}
        select
        value={formData.timezone || ""}
        onChange={(e) => updateField("timezone", e.target.value)}
        onBlur={() => markTouched("timezone")}
        error={!!requiredFieldError("timezone", formData.timezone, intl.formatMessage({ id: "establishmentForm.timezone" }))}
        helperText={requiredFieldError("timezone", formData.timezone, intl.formatMessage({ id: "establishmentForm.timezone" }))}
        required
      >
        {selectedCountry.timezones.map((timezone) => (
          <MenuItem key={timezone.value} value={timezone.value}>
            {timezone.label}
          </MenuItem>
        ))}
      </Input>
      {isEdit && formData.id && (
        <ThumbnailLogoCard
          establishmentId={formData.id}
          initialUrl={formData.thumbnailLogoUrl}
        />
      )}
      <Accordion
        expanded={channelsExpanded}
        onChange={(_, expanded) => setChannelsExpanded(expanded)}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>{intl.formatMessage({ id: "establishmentForm.channels" })} ({formData.channels?.length || 0})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({ id: "establishmentForm.channelsHint" })}
            </Typography>
            <RoleGuard
              allowedRoles={GLOBAL_ROLES}
              fallback={
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <span>
                    <IconButton
                      tooltip={intl.formatMessage({ id: "establishmentForm.channelsRestricted" })}
                      color="primary"
                      disabled
                    >
                      <Add />
                    </IconButton>
                  </span>
                </Box>
              }
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <IconButton
                  onClick={handleAddChannel}
                  color="primary"
                  aria-label={intl.formatMessage({ id: "establishmentForm.addChannel" })}
                  title={intl.formatMessage({ id: "establishmentForm.addChannel" })}
                >
                  <Add />
                </IconButton>
              </Box>
            </RoleGuard>

            {formData.channels?.map((channel, index) => {
              return (
                <Box
                  key={channel.id || `new-${index}`}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Input
                    label={`${intl.formatMessage({ id: "establishmentForm.channelName" })} ${index + 1}`}
                    value={channel.name}
                    onChange={(e) => handleUpdateChannelName(index, e.target.value)}
                    required
                  />
                  <RoleGuard
                    allowedRoles={GLOBAL_ROLES}
                    fallback={
                      <span>
                        <IconButton
                          tooltip={intl.formatMessage({ id: "establishmentForm.channelsRestricted" })}
                          disabled
                          sx={{ bgcolor: "action.disabledBackground", borderRadius: 1 }}
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    }
                  >
                    <IconButton
                      onClick={() => handleDeleteChannel(index)}
                      aria-label={intl.formatMessage({ id: "common.delete" })}
                      title={intl.formatMessage({ id: "common.delete" })}
                      sx={{
                        bgcolor: "error.main",
                        color: "error.contrastText",
                        borderRadius: 1,
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </RoleGuard>
                </Box>
              );
            })}
          </Box>
        </AccordionDetails>
      </Accordion>
      </FormContainer>

      <ConfirmDialog
        open={channelToDeleteIndex !== null}
        title={intl.formatMessage({ id: "common.delete" })}
        message={intl.formatMessage({ id: "establishmentForm.confirmDeleteChannel" })}
        confirmLabel={intl.formatMessage({ id: "common.delete" })}
        cancelLabel={intl.formatMessage({ id: "common.cancel" })}
        onClose={() => setChannelToDeleteIndex(null)}
        onConfirm={() => {
          if (channelToDeleteIndex === null) return;
          confirmDeleteChannel(channelToDeleteIndex);
          setChannelToDeleteIndex(null);
        }}
      />
    </>
  );
}
